import { store } from '../store/store';
import { addOnlineUser, removeOnlineUser } from '../store/slices/onlineUsersSlice';

/**
 * Thin wrapper around `socket.io-client` used by the frontend.
 *
 * Responsibilities:
 * - Maintain a single socket connection per active user.
 * - Translate presence events (`userOnline` / `userOffline`) into Redux updates.
 * - Provide a minimal `on/off/emit` API for other parts of the app.
 * - Perform best-effort reconnection with exponential backoff.
 */
class WebSocketService {
  /**
   * Executes logic in `constructor`.
   * @returns {void} No return value.
   */
  constructor() {
    this.socket = null;
    this.isConnected = false;
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
    this.reconnectDelay = 1000;
    this.eventListeners = new Map();
    this.reconnectTimer = null;
    this.userId = null;
    this.userType = null;
  }

  /**
   * Establish (or re-establish) a socket.io connection for the given user.
   *
   * If a connection already exists for the same user, this is a no-op.
   *
   * @param {string} userId
   * @param {string} userType
   */
  connect(userId, userType) {
    // Don't connect websocket during ghost mode — admin browsing a user's account
    // should NOT make that user appear as "online"
    if (localStorage.getItem('ghostMode') === 'true') {
      console.log('[WebSocket] Skipping connection — ghost mode active');
      return;
    }
    try {
      const state = store.getState();
      if (state.ghostLogin?.isGhostMode) {
        console.log('[WebSocket] Skipping connection — ghost mode active (Redux)');
        return;
      }
    } catch (_) { /* store not ready yet */ }

    // Don't connect if already connected with same user
    if (this.socket && this.socket.connected && this.userId === userId) {
      return;
    }

    // Clean up existing connection
    if (this.socket) {
      this.disconnect();
    }

    this.userId = userId;
    this.userType = userType;

    // Prefer an explicit socket URL, then fall back to API URL, then use environment defaults.
    const socketUrl = process.env.REACT_APP_SOCKET_URL || process.env.REACT_APP_API_URL || (process.env.NODE_ENV === 'production' ? 'https://speakable.online' : 'http://localhost:4000');

    const { io } = require('socket.io-client');
    
    this.socket = io(socketUrl, {
      query: { userId },
      withCredentials: true,
      transports: ['polling', 'websocket'],
      upgrade: true,
      rememberUpgrade: true,
      autoConnect: true,
      timeout: 20000,
      forceNew: false
    });

    this.setupEventListeners(userId, userType);
  }

  /**
   * Register core socket event handlers.
   *
   * This wires connection lifecycle events and presence events to the app store.
   *
   * @param {string} userId
   * @param {string} userType
   */
  setupEventListeners(userId, userType) {
    if (!this.socket) return;

    // Connection events
    this.socket.on('connect', () => {
     
      this.isConnected = true;
      this.reconnectAttempts = 0;
      
      // Clear any pending reconnection timer
      if (this.reconnectTimer) {
        clearTimeout(this.reconnectTimer);
        this.reconnectTimer = null;
      }
    });

    this.socket.on('disconnect', (reason) => {
      
      this.isConnected = false;
      
      // Only attempt reconnection for recoverable disconnects
      if (reason === 'io server disconnect') {
        // Server initiated disconnect, don't reconnect automatically
       
      } else if (reason !== 'io client disconnect') {
        // Client-side or network issues, attempt reconnection
        this.handleReconnect();
      }
    });

    this.socket.on('connect_error', (error) => {
      console.error('WebSocket connection error:', error);
      this.isConnected = false;
      this.handleReconnect();
    });

    // Online user events
    this.socket.on('userOnline', (userData) => {
      store.dispatch(addOnlineUser(userData));
    });

    this.socket.on('userOffline', (userData) => {
      store.dispatch(removeOnlineUser(userData));
    });

    // Error handling
    this.socket.on('error', (error) => {
      console.error('WebSocket error:', error, `User: ${userId} (${userType})`);
    });
  }

  // Connection management
  /**
   * Attempt to reconnect with exponential backoff up to a maximum number of attempts.
   *
   * This is used for network errors and unexpected disconnects.
   */
  handleReconnect() {
    // Clear any existing timer
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }

    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      
      return;
    }

    this.reconnectAttempts++;
    const delay = Math.min(this.reconnectDelay * Math.pow(2, this.reconnectAttempts), 30000);
    
   
    
    this.reconnectTimer = setTimeout(() => {
      if (!this.isConnected && this.userId) {
        this.connect(this.userId, this.userType);
      }
    }, delay);
  }

  /**
   * Disconnect the socket and reset all internal connection state.
   */
  disconnect() {
    // Clear reconnection timer
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }

    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
      this.isConnected = false;
      this.reconnectAttempts = 0;
      this.userId = null;
      this.userType = null;
    }
  }

  // Custom event listeners
  /**
   * Register a custom event handler on the underlying socket and remember it for later removal.
   * @param {string} event
   * @param {(payload: any) => void} callback
   */
  on(event, callback) {
    if (this.socket) {
      this.socket.on(event, callback);
      this.eventListeners.set(event, callback);
    }
  }

  /**
   * Remove a previously registered custom event handler.
   * @param {string} event
   */
  off(event) {
    if (this.socket && this.eventListeners.has(event)) {
      this.socket.off(event, this.eventListeners.get(event));
      this.eventListeners.delete(event);
    }
  }

  /**
   * Emit an event only when we believe the socket is connected.
   * @param {string} event
   * @param {any} data
   */
  emit(event, data) {
    if (this.socket && this.isConnected) {
      this.socket.emit(event, data);
    }
  }

  // Getters
  /**
   * Return true when the service is connected and the underlying socket is connected.
   */
  get connected() {
    return this.isConnected && this.socket?.connected;
  }

  /**
   * Socket.io connection id (if connected).
   */
  get socketId() {
    return this.socket?.id;
  }
}

// Create singleton instance
const websocketService = new WebSocketService();

export default websocketService;
